import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import {
    addQuestionsFromCsv,
    clearQuestionsCsvImportState,
    setQuestionsCsvImportErroneous,
    setQuestionsCsvImportFailed,
    setUploadedQuestionsCsvFilename,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/model/creatorElements.slice.ts';
import { mergeQuestionsFromCsvImport } from '@routes/sidebar/admin-center/creator/___pages___/elements/model/utils';
import {
    buildCsvIssuesFile,
    buildFatalCsvIssueFile,
    createCsvBlob,
    CSV_DEFAULT_DELIMITER,
} from '@shared/csv/use-caret-utils';
import type { QuestionsCsvImportResult } from '@shared/csv/use-questions-utils';
import {
    QUESTIONS_CSV_HEADER,
    QUESTIONS_CSV_IMPORT_STATUS_FAILED,
    QUESTIONS_CSV_IMPORT_STATUS_PARTIAL,
    QUESTIONS_CSV_IMPORT_STATUS_SUCCESS,
} from '@shared/csv/use-questions-utils';
import { questionsCsvDefinition, questionsCsvHandler } from '@shared/csv/use-questions-utils';
import { downloadBlob } from '@utils/download.ts';

import { useCaretCsv } from './useCaretCsv';

export const useQuestionsCsv = () => {
    const dispatch = useAppDispatch();
    const elementQuestions = useAppSelector((state) => state.adminCenterCreatorElements.elementQuestions);
    const selectedElement = useAppSelector((state) => state.adminCenterCreatorElements.selectedElement);
    const questionsCsvIssuesFileContent = useAppSelector(
        (state) => state.adminCenterCreatorElements.questionsCsvIssuesFileContent,
    );

    const EXPORT_FILE_NAME = selectedElement?.id ? `element-${selectedElement.id}-questions.csv` : 'questions.csv';
    const NO_NEW_QUESTIONS_TO_IMPORT =
        'No new questions to import. All rows were skipped or duplicate existing questions.';

    const { exportToFile, exportTemplate } = useCaretCsv(questionsCsvHandler, {
        items: elementQuestions,
        onImport: () => {},
        exportFileName: EXPORT_FILE_NAME,
    });

    const buildIssuesFileContent = useCallback(
        (issues: { rowNumber: number; error: string; rawLine: string }[]) =>
            buildCsvIssuesFile(CSV_DEFAULT_DELIMITER, QUESTIONS_CSV_HEADER, issues),
        [],
    );

    const importQuestionsCsvFile = useCallback(
        async (file: File): Promise<QuestionsCsvImportResult> => {
            dispatch(clearQuestionsCsvImportState());
            dispatch(setUploadedQuestionsCsvFilename(file.name));

            const result = await questionsCsvHandler.readFromFileWithValidation(file, elementQuestions);

            if (result.fatalError) {
                const issuesFileContent = buildFatalCsvIssueFile(
                    CSV_DEFAULT_DELIMITER,
                    QUESTIONS_CSV_HEADER,
                    2,
                    result.fatalError,
                );
                dispatch(setQuestionsCsvImportFailed({ issuesFileContent }));
                return { status: QUESTIONS_CSV_IMPORT_STATUS_FAILED, importedCount: 0, rejectedCount: 0 };
            }

            if (result.rowIssues.length > 0) {
                const issuesFileContent = buildIssuesFileContent(result.rowIssues);

                if (result.items.length > 0) {
                    dispatch(addQuestionsFromCsv(mergeQuestionsFromCsvImport(elementQuestions, result.items)));
                    dispatch(
                        setQuestionsCsvImportErroneous({
                            issuesFileContent,
                            rejectedCount: result.rowIssues.length,
                            importedCount: result.items.length,
                        }),
                    );
                    return {
                        status: QUESTIONS_CSV_IMPORT_STATUS_PARTIAL,
                        importedCount: result.items.length,
                        rejectedCount: result.rowIssues.length,
                    };
                }

                dispatch(setQuestionsCsvImportFailed({ issuesFileContent }));
                return {
                    status: QUESTIONS_CSV_IMPORT_STATUS_FAILED,
                    importedCount: 0,
                    rejectedCount: result.rowIssues.length,
                };
            }

            if (result.items.length === 0) {
                const issuesFileContent = buildFatalCsvIssueFile(
                    CSV_DEFAULT_DELIMITER,
                    QUESTIONS_CSV_HEADER,
                    2,
                    NO_NEW_QUESTIONS_TO_IMPORT,
                );
                dispatch(setQuestionsCsvImportFailed({ issuesFileContent }));
                return { status: QUESTIONS_CSV_IMPORT_STATUS_FAILED, importedCount: 0, rejectedCount: 0 };
            }

            dispatch(addQuestionsFromCsv(mergeQuestionsFromCsvImport(elementQuestions, result.items)));
            return {
                status: QUESTIONS_CSV_IMPORT_STATUS_SUCCESS,
                importedCount: result.items.length,
                rejectedCount: 0,
            };
        },
        [buildIssuesFileContent, dispatch, elementQuestions],
    );

    const downloadQuestionsCsvIssues = useCallback(() => {
        if (!questionsCsvIssuesFileContent) return;

        downloadBlob(
            createCsvBlob(questionsCsvIssuesFileContent),
            questionsCsvDefinition.issuesFileName ?? 'questions-import-errors.csv',
        );
    }, [questionsCsvIssuesFileContent]);

    return {
        importQuestionsCsvFile,
        exportQuestionsCsv: exportToFile,
        exportQuestionsCsvTemplate: exportTemplate,
        downloadQuestionsCsvIssues,
    };
};
